import { PropTypes } from 'typed/imports';
import { ComponentEventHandler1, PureComponent, ReactEventHandler, ReactEventHandler2 } from 'typed/components/utils';
import { ComponentElement, ReactElement } from 'react';
import { default as FormattedResource, Resource, isResource, resourcePropType } from '../FormattedResource/FormattedResource';
import { assertNever, toArray } from 'typed/utils';
import DropPanel, { CloseMode, SnapSide } from 'components/common/DropPanel/DropPanel';
import Spinner from 'components/common/Spinner/Spinner';
import Listbox, { LabelProps, Child as ListboxChild, GroupProps as ListboxGroupProps, OptionProps as ListboxOptionProps, Props as ListboxProps, valuePropType } from '../Listbox/Listbox';
import './Select.less';

export { valuePropType };

export class Label extends PureComponent<LabelProps> {
    static readonly displayName = 'Select.Label';

    static readonly propTypes = Listbox.Label.propTypes;

    static readonly defaultProps = Listbox.Label.defaultProps;

    render(): ReactElement {
        throw new Error('Select.Label can be used only inside Select');
    }
}

interface GroupProps<TValue> extends ListboxGroupProps<TValue> {
    children?: Children<TValue>;
}

export class Group<TValue> extends PureComponent<GroupProps<TValue>> {
    static readonly displayName = 'Select.Group';

    static readonly propTypes = Listbox.Group.propTypes;

    static readonly defaultProps = Listbox.Group.defaultProps;

    render(): ReactElement {
        throw new Error('Select.Group can be used only inside Select');
    }
}

interface OptionProps<TValue> extends ListboxOptionProps<TValue> {
    summary?: ReactElement | Resource;
}

export class Option<TValue> extends PureComponent<OptionProps<TValue>> {
    static readonly displayName = 'Select.Option';

    static readonly propTypes = {
        ...Listbox.Option.propTypes,
        summary: PropTypes.oneOfType([resourcePropType, PropTypes.element])
    };

    static readonly defaultProps = Listbox.Option.defaultProps;

    render(): ReactElement {
        throw new Error('Select.Option can be used only inside Select');
    }
}

type Child<TValue> =
    | undefined | null
    | ComponentElement<LabelProps, Label>
    | ComponentElement<GroupProps<TValue>, Group<TValue>>
    | ComponentElement<OptionProps<TValue>, Option<TValue>>
    | ReactElement /* TODO validate children at compile time? for some reason `<Option />` has type of Element, with no information of it's origin */;

type Children<TValue> = Child<TValue> | Child<TValue>[] | (Child<TValue> | Child<TValue>[])[];

const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isLabel = (child: ReactElement): child is ReactElement<LabelProps> => !!child && typeof child === 'object' && child.type === Label,
    isGroup = <TValue extends any>(child: ReactElement): child is ReactElement<GroupProps<TValue>> => !!child && typeof child === 'object' && child.type === Group,
    isOption = <TValue extends any>(child: ReactElement): child is ReactElement<OptionProps<TValue>> => !!child && typeof child === 'object' && child.type === Option;

function keyify<TValue>(child: Child<TValue>, groupKey: string): { key: string; child: Child<TValue>; };
function keyify<TValue>(child: Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; }[];
function keyify<TValue>(child: Child<TValue> | Child<TValue>[], groupKey: string): { key: string; child: Child<TValue>; } | { key: string; child: Child<TValue>; }[] {
    return Array.isArray(child) ?
        child.map((subchild, subindex) => keyify(subchild, `${groupKey}.${subindex}`)) :
        {
            key: child?.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

interface CommonProps<TValue> extends Omit<ListboxProps<TValue>, 'onChange'> {
    placeholder?: ReactElement | Resource;
    pending?: boolean;
    opened?: boolean;
    scrollElementId?: string;
    onOpen?: ComponentEventHandler1<unknown>;
    onClose?: ComponentEventHandler1<unknown>;
    children?: Children<TValue>;
}

interface ClearableProps<TValue> extends CommonProps<TValue> {
    clearable?: true;
    onChange?: ComponentEventHandler1<TValue | null>;
}

interface NonClearableProps<TValue> extends CommonProps<TValue> {
    clearable: false;
    onChange?: ComponentEventHandler1<TValue>;
}

export type Props<TValue> = ClearableProps<TValue> | NonClearableProps<TValue>;

interface State {
    opened: boolean;
}

export default class Select<TValue extends string | number | boolean> extends PureComponent<Props<TValue>, State> {
    static readonly valuePropType = valuePropType;
    static readonly Label = Label;
    static readonly Group = Group;
    static readonly Option = Option;

    static readonly propTypes = {
        ...Listbox.propTypes,

        placeholder: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        clearable: PropTypes.bool,
        opened: PropTypes.bool,
        scrollElementId: PropTypes.string,
        onOpen: PropTypes.func,
        onChange: PropTypes.func,
        onClose: PropTypes.func
    };

    static readonly defaultProps = {
        ...Listbox.defaultProps,

        clearable: true,
        opened: undefined
    };

    state: State = { opened: false };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props<TValue>) {
        if (nextProps.opened !== undefined) {
            this.setState({ opened: nextProps.opened });
        }

        if (DEBUG) {
            this._validateChildren(nextProps);
        }
    }

    private _validateChildren(props: Props<TValue> = this.props, children: Children<TValue> = props.children, usedValues: Set<TValue> = new Set<TValue>(), usedGroups: Set<TValue> = new Set<TValue>()) {
        toArray(children || [])
            .flatten()
            .forEach((child) => {
                if (!child || isImpossibleChild(child) || isLabel(child)) { return; }

                if (isGroup<TValue>(child) || isOption<TValue>(child)) {
                    const
                        value = isGroup<TValue>(child) ? child.props.id : child.props.value,
                        set = !isGroup<TValue>(child) || props.groupSelectionEnabled ? usedValues : usedGroups;
                    if (set.has(value)) {
                        throw new Error(`Duplicate value in ${child.type.displayName} ${isGroup<TValue>(child) && props.groupSelectionEnabled ? 'as Option' : ''}: ${value}`);
                    }
                    set.add(value);


                    if (isGroup<TValue>(child) && child.props.children) {
                        this._validateChildren(props, child.props.children, usedValues, usedGroups);
                    }
                    return;
                }

                assertNever(child, 'Children of Select must be instances of Select.Label, Select.Group or Select.Option.');
            });
    }

    _onOpen: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: true });

        if (this.props.onOpen) {
            this.props.onOpen(this, event);
        }
    };

    _onChange: ComponentEventHandler1<TValue> = (_sender, value) => {
        if (this.props.onChange) {
            this.props.onChange(this, value);
        }

        this._onClose(this, null);
    };

    _onKeyDown: ReactEventHandler2<TValue[], unknown> = (values, _sender, event) => {
        if (this.props.onChange && !this.props.disabled) {
            const index = values.indexOf(this.props.value!);
            if ((event as any).key === 'ArrowDown' && index < values.length - 1) {
                this.props.onChange(this, values[index + 1]);
                event.preventDefault();
            }
            if ((event as any).key === 'ArrowUp' && index > 0) {
                this.props.onChange(this, values[index - 1]);
                event.preventDefault();
            }
        }
    };

    _onClear: ReactEventHandler = (event) => {
        if (!this.props.disabled && this.props.clearable !== false) {
            if (this.props.onChange) {
                this.props.onChange(this, null);
            }

            this._onClose(this, null);
        }

        event.stopPropagation();
    };

    _onClose: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: false });

        if (this.props.onClose) {
            this.props.onClose(this, event);
        }
    };

    _renderChildren(children: Children<TValue>, statsToFill: { values: TValue[]; selectedOption: Child<TValue>; } = { values: [], selectedOption: null }, disabled = false) {
        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child<TValue>[], `${index}`)) // TODO how to describe infinitely nested arrays?
            .flatten()
            .map(({ child, key }) => {
                if (!child || isImpossibleChild(child)) { return undefined; }

                if (isLabel(child)) { return <Listbox.Label key={key} {...child.props}>{child.props.children}</Listbox.Label> as ListboxChild<TValue>; }

                if (isGroup<TValue>(child)) {
                    if (this.props.groupSelectionEnabled) {
                        if (!child.props.disabled) {
                            statsToFill.values.push(child.props.id);
                        }

                        if (child.props.id === this.props.value) {
                            statsToFill.selectedOption = child;
                        }
                    }

                    return (
                        <Listbox.Group key={key} {...child.props}>
                            {this._renderChildren(child.props.children, statsToFill, child.props.disabled)}
                        </Listbox.Group>
                    ) as ListboxChild<TValue>;
                }

                if (isOption<TValue>(child)) {
                    const { summary, children, ...lbProps } = child.props;

                    if (!disabled && !child.props.disabled) {
                        statsToFill.values.push(lbProps.value);
                    }

                    if (lbProps.value === this.props.value) {
                        statsToFill.selectedOption = child;
                    }

                    return <Listbox.Option key={key} {...lbProps}>{children}</Listbox.Option> as ListboxChild<TValue>;
                }

                return assertNever(child);
            });
    }

    render() {
        const
            { placeholder, clearable, opened, pending, disabled, className, scrollElementId, onOpen, onChange, onClose, children, ...lbProps } = this.props,
            stats: { values: TValue[]; selectedOption: Child<TValue>; } = { values: [], selectedOption: null },
            lbChildren = this._renderChildren(children, stats),
            { selectedOption } = stats,
            selectedOptionSummary = !selectedOption ? null :
            isGroup<TValue>(selectedOption) ? selectedOption.props.title :
            isOption<TValue>(selectedOption) && selectedOption.props.summary ? selectedOption.props.summary :
            selectedOption.props.children,
            title = selectedOptionSummary || placeholder;

        return (
            <DropPanel
                opened={this.state.opened}
                className={`select ${className}`}
                disabled={disabled || !lbChildren.length}
                title={
                    !pending ?
                        <span className={selectedOptionSummary ? 'content' : 'placeholder'}>{isResource(title) ? <FormattedResource resource={title} /> : title}</span> :
                        <div className="skeleton text" />
                }
                icon={!disabled && selectedOptionSummary && clearable ? <span className="fa fa-times" onClick={this._onClear} /> : DropPanel.AUTO_ICON}
                snap={[SnapSide.LEFT, SnapSide.RIGHT]}
                closeMode={CloseMode.OUTSIDE}
                scrollElementId={scrollElementId ? scrollElementId : undefined}
                onKeyDown={this._onKeyDown.bind(this, stats.values)}
                onOpen={this._onOpen}
                onClose={this._onClose}>
                {
                    !pending ?
                        <Listbox {...lbProps} onChange={this._onChange}>{lbChildren}</Listbox> :
                        <Spinner config={{ scale: 0.5 }} />
                }
            </DropPanel>
        );
    }
}
